import { Controller } from "@hotwired/stimulus"

// Drives the site form's live preview. Two modes:
//
//  - REAL (edit / embed_code): when a signed demo token is present it boots the
//    actual widget.js inline against the site's API key. Appearance edits are
//    forwarded to the mounted widget via window.__widgetoInline.setAppearance,
//    so unsaved color/theme/variant/bot-name/welcome changes show up immediately.
//  - STATIC (new site, no key/token): updates a self-contained appearance
//    mockup (container/botName/welcome targets) — no backend chat.
export default class extends Controller {
  static targets = ["container", "botName", "welcome", "colorPicker", "colorText", "frame"]
  static values = {
    demoToken: String,
    apiKey: String,
    apiUrl: String,
    inlineSelector: String,
    language: String
  }

  connect() {
    // Defer boot to the next animation frame so sibling/child controllers
    // (e.g. tabs) have already connected and set their initial state.
    if (this.real) requestAnimationFrame(() => this.bootRealWidget())
  }

  // True once a demo token is wired — i.e. the real widget is (being) booted.
  get real() {
    return this.hasDemoTokenValue && this.demoTokenValue.length > 0
  }

  // Owner edits the bot copy per language; only the input matching the language
  // the widget actually loaded should drive the preview.
  get previewLanguage() {
    return this.hasLanguageValue && this.languageValue ? this.languageValue : "en"
  }

  colorChanged(event) {
    const color = event.target.value
    // The picker and the text field share the same param name (site[primary_color]);
    // keep them in lockstep so the un-edited one doesn't overwrite the change on submit.
    if (this.hasColorPickerTarget && this.colorPickerTarget !== event.target) this.colorPickerTarget.value = color
    if (this.hasColorTextTarget && this.colorTextTarget !== event.target) this.colorTextTarget.value = color
    if (this.real) {
      this.setRealAppearance({ color })
      return
    }
    this.containerTarget.style.setProperty("--preview-brand", color)
    this.containerTarget.style.setProperty("--preview-contrast", this.contrastColor(color))
  }

  // Called by color inputs and direction radios inside a theme panel.
  // Reads all current color values from that panel and pushes them to the
  // live inline widget via setAppearance({ themeLight }) or ({ themeDark }).
  themeColorChanged(event) {
    if (!this.real) return;
    const panel = event.target.closest('[data-panel]');
    if (!panel) return;
    const code = panel.dataset.themeCode; // 'light' or 'dark'
    if (!code) return;

    const colors = {};
    panel.querySelectorAll('input[type=text][name^="site[theme_"]').forEach((inp) => {
      // A disabled field is not submitted, so the preview must not show it
      // either (the launcher's colours while it follows the header).
      if (inp.disabled) return;
      const m = inp.name.match(/\[([^\]]+)\]$/);
      if (m) colors[m[1]] = inp.value;
    });
    // direction radio
    const dirRadio = panel.querySelector('input[type=radio][name$="[mainColorDirection]"]:checked');
    if (dirRadio) colors.mainColorDirection = dirRadio.value;

    const payload = { enabled: true, colors };
    if (code === 'light') {
      this.setRealAppearance({ themeLight: payload });
    } else {
      this.setRealAppearance({ themeDark: payload });
    }
  }

  // Style variant radio cards. The widget opens its panel on a variant change,
  // since a variant is only visible inside it. The static new-site mockup has
  // no variants, so there is nothing to update without a real widget.
  variantChanged(event) {
    if (!this.real) return
    this.setRealAppearance({ variant: event.target.value })
  }

  // Toggles the theme so the preview surfaces switch light/dark/system live.
  themeChanged(event) {
    const theme = event.target.value || "light"
    if (this.real) {
      this.setRealAppearance({ theme })
      return
    }
    this.containerTarget.classList.remove("preview-theme-light", "preview-theme-dark", "preview-theme-system")
    this.containerTarget.classList.add(`preview-theme-${theme}`)
  }

  // Called when a theme tab button is activated. Switches the preview to that
  // theme only if the theme is enabled on the site.
  tabThemeChanged(event) {
    const btn = event.currentTarget
    const code = btn.dataset.themeCode       // 'light', 'dark' or 'system'
    const enabled = btn.dataset.themeEnabled !== "false"
    if (!code || !enabled) return
    this.syncSnippetTheme(code)
    if (!this.real) return
    this.setRealAppearance({ theme: code })
  }

  // The API-key picker cannot be a form (it renders inside the configuration
  // form), so it navigates instead. Reloads with the chosen key so both the
  // snippet and the booted preview use it.
  apiKeyChanged(event) {
    const url = new URL(window.location.href)
    url.searchParams.set("api_key_id", event.target.value)
    window.location.assign(url.toString())
  }

  // Keep the install snippet's data-theme matching what is being previewed, so
  // copying it boots the widget in the theme the owner just looked at. The
  // snippet sits outside the form, which is why this controller wraps the page.
  // All copies are updated: the install-guide modals each render their own.
  syncSnippetTheme(theme) {
    this.element.querySelectorAll("[data-embed-code-target='snippet']").forEach((snippet) => {
      snippet.textContent = snippet.textContent
        // The plain <script> tag…
        .replace(/data-theme="[^"]*"/, `data-theme="${theme}"`)
        // …and the Tag Manager form, which sets the same attribute in JS.
        .replace(/("data-theme",\s*")[^"]*"/, `$1${theme}"`)
    })
  }

  botNameChanged(event) {
    if (event.target.dataset.lang && event.target.dataset.lang !== this.previewLanguage) return
    const botName = event.target.value
    if (this.real) {
      this.setRealAppearance({ botName })
      return
    }
    if (botName.trim()) this.botNameTarget.textContent = botName
  }

  welcomeChanged(event) {
    if (event.target.dataset.lang && event.target.dataset.lang !== this.previewLanguage) return
    const welcomeMessage = event.target.value
    if (this.real) {
      this.setRealAppearance({ welcomeMessage })
      return
    }
    if (welcomeMessage.trim()) this.welcomeTarget.textContent = welcomeMessage
  }

  // The owner-only bridge exposed by widget.js, wherever the widget was mounted.
  // widget.js loads its config asynchronously, so it may not exist yet for the
  // first few keystrokes — callers silently skip until it does.
  get bridge() {
    const win = this.hasFrameTarget ? this.frameTarget.contentWindow : window
    return win?.__widgetoInline
  }

  // Forward an appearance patch to the live widget if it has finished booting.
  setRealAppearance(attrs) {
    this.bridge?.setAppearance(attrs)
  }

  // Toggle element / position / avatar live on the launcher. These come from the
  // server config, so without this the owner would have to save before seeing
  // any change to the closed bubble.
  launcherChanged() {
    if (!this.real) return
    const checkedAvatar = this.element.querySelector("input[name='site[toggle_avatar]']:checked")
    this.bridge?.setLauncher({
      element: this.element.querySelector("select[name='site[toggle_element]']")?.value,
      position: this.element.querySelector("select[name='site[toggle_position]']")?.value,
      // The radio's sibling image already carries an absolute URL — no building here.
      avatarUrl: checkedAvatar?.parentElement.querySelector("img")?.src || null
    })
  }

  // Black or white depending on the luminance of the given hex color.
  contrastColor(hex) {
    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return "#ffffff"
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#000000" : "#ffffff"
  }

  // Inject widget.js with the demo token so chat requests are sent with the
  // X-Demo-Token header (the widget reads data-demo-token). Mounts into the
  // preview iframe when there is one, otherwise onto the host page.
  bootRealWidget() {
    if (!this.apiKeyValue) return

    const attrs = {
      "data-api-key": this.apiKeyValue,
      "data-api-url": this.apiUrlValue,
      "data-demo-token": this.demoTokenValue
    }
    if (this.hasLanguageValue && this.languageValue) attrs["data-language"] = this.languageValue
    if (this.inlineSelectorValue && this.inlineSelectorValue.length > 0) {
      attrs["data-inline"] = this.inlineSelectorValue
    }
    // Boot with the theme matching the active theme tab (if present), so the
    // widget starts with the right colors without a post-boot setAppearance call.
    const activeThemeTab = this.element.querySelector("[data-theme-code][data-tabs-target='tab'].tab-selected")
    if (activeThemeTab?.dataset.themeEnabled !== "false" && activeThemeTab?.dataset.themeCode) {
      attrs["data-theme"] = activeThemeTab.dataset.themeCode
    }

    if (this.hasFrameTarget) {
      this.bootInFrame(attrs)
      return
    }

    const script = document.createElement("script")
    script.src = `${this.apiUrlValue}/widget.js`
    Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v))
    document.body.appendChild(script)
  }

  // Writes a bare stand-in for a customer page into the preview iframe. An
  // iframe is its own viewport, so the launcher's position:fixed resolves
  // against it — the only way bottom-center and the rotated side badges preview
  // truthfully. Same-origin (no sandbox attribute) so the parent can reach
  // contentWindow.__widgetoInline and the widget's config fetch keeps the session.
  bootInFrame(attrs) {
    const script = `<script src="${this.apiUrlValue}/widget.js" ${
      Object.entries(attrs).map(([k, v]) => `${k}="${String(v).replace(/"/g, "&quot;")}"`).join(" ")
    }><\/script>`

    this.frameTarget.srcdoc = `<!doctype html><html><head><meta charset="utf-8"></head>` +
      `<body style="margin:0;height:100vh;background:#ffffff;font-family:system-ui,sans-serif">` +
      `${this.pageSkeleton()}` +
      `<div id="widgeto-inline-mount" style="width:100%;height:100%"></div>${script}</body></html>`
  }

  // Faint stand-in for the customer's page content. Purely scene-setting: it
  // gives the launcher something to sit on top of so the owner can judge
  // contrast and placement instead of reading it against a blank surface.
  pageSkeleton() {
    const bar = `<div style="height:44px;background:#e9edf3;border-bottom:1px solid #dfe4ec"></div>`
    const line = (w, h = 10, mt = 10) =>
      `<div style="width:${w};height:${h}px;margin-top:${mt}px;border-radius:4px;background:#e9edf3"></div>`
    const card = `<div style="flex:1;height:76px;border-radius:8px;background:#eef1f6"></div>`

    return `<div data-preview-skeleton aria-hidden="true" style="position:absolute;inset:0;overflow:hidden">
      ${bar}
      <div style="padding:20px">
        ${line("52%", 18, 0)}${line("86%")}${line("74%")}${line("64%")}
        <div style="display:flex;gap:12px;margin-top:22px">${card}${card}</div>
        ${line("80%", 10, 22)}${line("68%")}
      </div>
    </div>`
  }
}
